import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as signalR from '@microsoft/signalr';
import { OAuthService } from 'angular-oauth2-oidc';

/**
 * Ties the hub connection to the auth session.
 *
 * - Starts the connection only while a valid access token exists, so the login page and a logged-out
 *   tab never hit the negotiate endpoint.
 * - `token_received` (password login, silent refresh) starts the connection if it is down.
 * - `logout` stops it.
 * - Transient drops are handled by `withAutomaticReconnect()` on the connection itself; if that gives up
 *   (`onclose`), or the initial start fails, this retries with backoff for as long as the token stays valid.
 *
 * The `accessTokenFactory` on the connection reads the current token on every negotiate, so a reconnect
 * after a silent refresh picks up the new token without any help from here.
 */
@Injectable({ providedIn: 'root' })
export class HubConnectionManager {
  private static readonly RETRY_DELAYS_MS = [5_000, 10_000, 30_000, 60_000];

  private readonly connection = inject(signalR.HubConnection);
  private readonly oAuthService = inject(OAuthService);
  private readonly destroyRef = inject(DestroyRef);

  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private failedAttempts = 0;
  private initialized = false;

  /** Idempotent. Wire auth events and connect if a session already exists. */
  initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    this.connection.onclose(() => this.scheduleRetry());

    this.oAuthService.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event.type === 'token_received') {
          this.connectIfAuthenticated();
        } else if (event.type === 'logout') {
          this.disconnect();
        }
      });

    this.connectIfAuthenticated();
  }

  private connectIfAuthenticated(): void {
    this.clearRetry();

    if (!this.oAuthService.hasValidAccessToken()) {
      return;
    }
    if (this.connection.state !== signalR.HubConnectionState.Disconnected) {
      return;
    }

    this.connection
      .start()
      .then(() => {
        this.failedAttempts = 0;
        console.log('SignalR connected.');
      })
      .catch((err) => {
        console.warn('SignalR connection failed.', err);
        this.scheduleRetry();
      });
  }

  private disconnect(): void {
    this.clearRetry();
    this.failedAttempts = 0;

    if (this.connection.state === signalR.HubConnectionState.Disconnected) {
      return;
    }

    // onclose fires after stop(); scheduleRetry then finds no valid token and does nothing.
    this.connection.stop().catch((err) => console.warn('SignalR stop failed.', err));
  }

  private scheduleRetry(): void {
    this.clearRetry();

    if (!this.oAuthService.hasValidAccessToken()) {
      return;
    }

    const delays = HubConnectionManager.RETRY_DELAYS_MS;
    const delay = delays[Math.min(this.failedAttempts, delays.length - 1)];
    this.failedAttempts++;

    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      this.connectIfAuthenticated();
    }, delay);
  }

  private clearRetry(): void {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }
}
